import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import { FramingComponentType } from "building-types/framing/framingComponentTypes";
import type { HeaderAssemblyEntity } from "building-types/framing/headerAssemblyTypes";
import type {
  LumberLengthDescriptor,
  StandardLumberCounterSet,
  LumberRail,
  LumberPiecesByProfile,
} from "building-types/framing/lumberTypes";
import { LumberProfileType, type LumberProfileDescriptor } from "building-types/framing/lumberTypes";
import { LumberProfileCatalog } from "building-types/framing/lumberTypes";
import { lumberProfileDescriptorNew } from "building-types/framing/lumberTypes";
import type { OpeningHandle, RoomHandle } from "building-types/handles/docEntityHandleTypes";
import type { TargetedOpeningSettings } from "building-types/openings/opening";
import type { HeaderFramingSettings } from "building-types/settings/headerFramingSettings";
import type { PartialHomeSettings } from "building-types/settings/homeSettings";
import type { WallFramingOpeningSettings } from "building-types/settings/wallFramingOpeningSettings";
import type { WallCut } from "building-types/walls/wallCutTypes";
import type {
  Blocking,
  Framing,
  Header,
  Plate,
  ResolvedRoughOpeningFramingSettings,
  RoughOpening,
  Stud,
  WallFramingMeta,
} from "building-types/walls/wallFramingTypes";
import { OpeningSource, WallFramingType } from "building-types/walls/wallFramingTypes";
import type { WallGap } from "building-types/walls/wallGap";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import type { Table } from "common/data/table";
import { f2i } from "doc/measurementUtils";
import { getDocEntityIdFromGeneratedRoomLayoutEligibleEntity } from "doc/rooms/layout/roomLayoutTools";
import { defaultWallFramingOpeningSettings } from "doc/settings/defaultSettings";
import {
  getLumberPiece,
  HeaderLumberLengths,
  isSameProfileDescriptor,
  PlateLumberLengths,
  StudLumberLengths,
} from "doc/utils/lumberTools";
import { unionFramingSettings } from "doc/utils/unionOpeningFramingSettings";
import { DIST_EPSILON } from "geom/core";

const DOUBLE_SILL_PLATE_MIN_WIDTH = 6 * 12; // 6 feet

function openingHandleFromPlacement(
  opening: GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut,
): OpeningHandle | undefined {
  if (opening.type === "WALL_CUT") {
    return undefined;
  }
  const openingId = getDocEntityIdFromGeneratedRoomLayoutEligibleEntity(opening);
  return openingId !== undefined ? { type: "OPENING", id: openingId } : undefined;
}

function sillPlateCountForWallGap({ start, end }: WallGap): number {
  const width = end - start;

  return width >= DOUBLE_SILL_PLATE_MIN_WIDTH ? 2 : 1;
}

function roughOpeningNew({
  headerType,
  start,
  end,
  extendedStart,
  extendedEnd,
  bottom,
  top,
  sillPlateCount,
  headerProfile,
  headerAssemblyName,
  headerAssemblyId,
  numberOfJackStuds,
  numberOfKingStuds,
  hasCrippleStudEnds,
  hasCrippleStudsBelowOpening,
  hasCrippleStudsAboveOpening,
  hasTopHeaderSillPlate,
  headerCount,
  resolvedOpeningFraming,
  adjacentRooms,
  openingHandle,
}: RoughOpening): RoughOpening {
  return {
    headerType,
    start,
    end,
    extendedStart,
    extendedEnd,
    bottom,
    top,
    sillPlateCount,
    headerProfile,
    headerAssemblyName,
    headerAssemblyId,
    numberOfJackStuds,
    numberOfKingStuds,
    hasCrippleStudEnds,
    hasCrippleStudsBelowOpening,
    hasCrippleStudsAboveOpening,
    hasTopHeaderSillPlate,
    headerCount,
    resolvedOpeningFraming,
    adjacentRooms,
    openingHandle,
  };
}

interface MakeRoughOpeningParams {
  readonly openings: (GeneratedWindowPlacement | GeneratedDoorPlacement | WallCut)[];
  readonly gap: WallGap;
  readonly studThickness: number;
  readonly wallStripBottom: number;
  readonly levelFloorElevation: number;
  readonly isGable?: boolean;
  readonly targetedOpeningSettings: TargetedOpeningSettings;
  readonly headerAssemblies: Table<HeaderAssemblyEntity>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly headerFramingSettings: HeaderFramingSettings;
}

export function resolveRoughOpeningFramingSettings({
  openings,
  gap,
  isDoor,
  targetedOpeningSettings,
  headerAssemblies,
  framingComponents,
  headerFramingSettings,
}: Pick<
  MakeRoughOpeningParams,
  "openings" | "targetedOpeningSettings" | "headerAssemblies" | "framingComponents" | "headerFramingSettings"
> & {
  readonly gap: WallGap;
  readonly isDoor: boolean;
}): ResolvedRoughOpeningFramingSettings {
  const targetedSettingsField: keyof PartialHomeSettings = isDoor ? "doorFraming" : "windowFraming";
  const openingCatalogsFraming = openings
    .map((opening) => {
      if (opening.type === "WALL_CUT") {
        return undefined;
      }
      return opening.descriptor?.framing;
    })
    .filter(Boolean);
  const targetedOpeningFramingSettings = openings
    .map((opening) => {
      if (opening.type === "WALL_CUT") {
        return undefined;
      }
      const openingId = getDocEntityIdFromGeneratedRoomLayoutEligibleEntity(opening);
      if (!openingId) {
        return undefined;
      }
      return targetedOpeningSettings[openingId]?.[targetedSettingsField];
    })
    .filter(Boolean);
  const openingsFraming = openings
    .map((opening) => {
      if (opening.type === "WALL_CUT") {
        return undefined;
      }
      return opening.framing;
    })
    .filter(Boolean);

  const catalogFramingUnion = unionFramingSettings(openingCatalogsFraming);
  const targetedSettingsUnion = unionFramingSettings(targetedOpeningFramingSettings);
  const openingUnion = unionFramingSettings(openingsFraming);

  const openingSettings: WallFramingOpeningSettings = {
    ...defaultWallFramingOpeningSettings,
    ...catalogFramingUnion,
    ...targetedSettingsUnion,
    ...openingUnion,
  };

  const {
    numberOfKingStuds,
    numberOfJackStuds: numberOfJackStudsFromSettings,
    roughOpeningBorder,
    hasHeaderAssembly,
    headerAssemblyId: mergedHeaderAssemblyId,
  } = openingSettings;
  const gapLength = gap.end - gap.start + 2 * roughOpeningBorder;

  if (hasHeaderAssembly === false) {
    const { hasCrippleStudEnds, hasCrippleStudsBelowOpening, hasCrippleStudsAboveOpening, hasTopHeaderSillPlate } =
      openingSettings;

    return {
      openingSettings,
      gapLength,
      headerAssemblyId: null,
      headerProfile: getHeaderLumberProfile(gapLength),
      headerAssemblyName: undefined,
      numberOfJackStuds: numberOfJackStudsFromSettings ?? 1,
      numberOfKingStuds,
      roughOpeningBorder,
      headerCount: 0,
      hasCrippleStudEnds,
      hasCrippleStudsBelowOpening,
      hasCrippleStudsAboveOpening,
      hasTopHeaderSillPlate,
    };
  }

  let {
    headerProfile,
    headerAssemblyName,
    headerAssemblyId: resolvedHeaderAssemblyId,
    jackStudsFromHeader: numberOfJackStuds,
    headerCount,
  } = getHeaderLumberProfileFromHeaderFramingSettings(
    gapLength,
    headerFramingSettings,
    headerAssemblies,
    framingComponents,
  );

  if (mergedHeaderAssemblyId !== undefined) {
    const headerAssembly = headerAssemblies[mergedHeaderAssemblyId];
    const headerComponent =
      headerAssembly !== undefined ? framingComponents[headerAssembly.headerComponentId] : undefined;
    if (headerAssembly === undefined || headerComponent === undefined) {
      console.warn(`Skipping invalid opening header assembly reference: ${mergedHeaderAssemblyId}`);
    } else {
      headerProfile = getLumberProfileDescriptorFromHeaderComponent(headerComponent);
      headerCount = headerAssembly.headerCount;
      headerAssemblyName = headerAssembly.name;
      resolvedHeaderAssemblyId = headerAssembly.id;
    }
  }
  if (numberOfJackStudsFromSettings !== undefined) {
    numberOfJackStuds = numberOfJackStudsFromSettings;
  }
  const { hasCrippleStudEnds, hasCrippleStudsBelowOpening, hasCrippleStudsAboveOpening, hasTopHeaderSillPlate } =
    openingSettings;

  const resolvedHeaderProfile = headerProfile ?? getHeaderLumberProfile(gapLength);

  return {
    openingSettings,
    gapLength,
    headerAssemblyId: resolvedHeaderAssemblyId,
    headerProfile: resolvedHeaderProfile,
    headerAssemblyName,
    numberOfJackStuds,
    numberOfKingStuds,
    roughOpeningBorder,
    headerCount,
    hasCrippleStudEnds,
    hasCrippleStudsBelowOpening,
    hasCrippleStudsAboveOpening,
    hasTopHeaderSillPlate,
  };
}

export function makeRoughOpening({
  openings,
  gap,
  studThickness,
  wallStripBottom,
  levelFloorElevation,
  isGable = false,
  targetedOpeningSettings,
  headerAssemblies,
  framingComponents,
  headerFramingSettings,
}: MakeRoughOpeningParams): RoughOpening {
  const { start, end, bottom, height } = gap;

  // @TODO: this function needs to be rewritten as framing around different opening types need to be handled differently
  // a window and a door adjacent to each other are creating a big opening which is missing window framing entirely
  // gables do not have doors
  const isDoor = !isGable && bottom <= DIST_EPSILON;

  /**
   * This settings merge logic takes place outside of getMergedWindowSettings & getMergedDoorSettings
   * b/c these calculations need to have much further down stream in the opening generation process
   */
  const {
    headerAssemblyId,
    headerProfile,
    headerAssemblyName,
    numberOfJackStuds,
    numberOfKingStuds,
    roughOpeningBorder,
    headerCount,
    hasCrippleStudEnds,
    hasCrippleStudsBelowOpening,
    hasCrippleStudsAboveOpening,
    hasTopHeaderSillPlate,
  } = resolveRoughOpeningFramingSettings({
    openings,
    gap,
    isDoor,
    targetedOpeningSettings,
    headerAssemblies,
    framingComponents,
    headerFramingSettings,
  });

  const kingStudCount = numberOfKingStuds;
  const jackStudOffset = numberOfJackStuds * studThickness;
  const kingStudsOffset = kingStudCount * studThickness;
  const gapExtension = jackStudOffset + kingStudsOffset;

  const roughOpeningBottom = Math.max(bottom - roughOpeningBorder, 0) + wallStripBottom;
  const roughOpeningTop = bottom + height + roughOpeningBorder + (isDoor ? levelFloorElevation : wallStripBottom);
  const { type: openingType } = openings[0];

  let frontRoomHandle: RoomHandle | null = null;
  let backRoomHandle: RoomHandle | null = null;
  let headerType: OpeningSource | undefined = undefined;
  let openingHandle: OpeningHandle | undefined;
  if (!isGable) {
    switch (openingType) {
      case "WALL_CUT":
        frontRoomHandle = openings[0].opening?.frontRoom?.roomHandle || null;
        backRoomHandle = openings[0].opening?.backRoom?.roomHandle || null;
        break;
      case "WINDOW":
      case "WINDOW_V2":
        openingHandle = openingHandleFromPlacement(openings[0]);
        headerType = OpeningSource.WINDOW;
        frontRoomHandle = openings[0].frontRoom?.roomHandle || null;
        backRoomHandle = openings[0].backRoom?.roomHandle || null;
        break;
      case "DOOR":
      case "DOOR_V2":
        openingHandle = openingHandleFromPlacement(openings[0]);
        headerType = OpeningSource.DOOR;
        frontRoomHandle = openings[0].frontRoom?.roomHandle || null;
        backRoomHandle = openings[0].backRoom?.roomHandle || null;
        break;
      default:
        console.error("Unknown opening type", openingType);
        break;
    }
  }

  return roughOpeningNew({
    headerType,
    start: start - roughOpeningBorder,
    end: end + roughOpeningBorder,
    extendedStart: start - roughOpeningBorder - gapExtension,
    extendedEnd: end + roughOpeningBorder + gapExtension,
    bottom: roughOpeningBottom,
    top: roughOpeningTop,
    sillPlateCount: sillPlateCountForWallGap(gap),
    headerProfile,
    headerAssemblyName,
    headerAssemblyId,
    numberOfJackStuds,
    numberOfKingStuds,
    hasCrippleStudEnds,
    hasCrippleStudsBelowOpening,
    hasCrippleStudsAboveOpening,
    hasTopHeaderSillPlate,
    headerCount,
    resolvedOpeningFraming: {
      headerAssemblyId,
      headerAssemblyName,
      numberOfJackStuds,
      numberOfKingStuds,
      roughOpeningBorder,
      headerCount,
      hasCrippleStudEnds,
      hasCrippleStudsBelowOpening,
      hasCrippleStudsAboveOpening,
      hasTopHeaderSillPlate,
    },
    adjacentRooms: [frontRoomHandle, backRoomHandle],
    openingHandle,
  });
}

export function getLumberProfileDescriptorFromHeaderComponent(
  headerComponent: FramingComponentEntity,
): LumberProfileDescriptor {
  switch (headerComponent.type) {
    case FramingComponentType.DIMENSIONAL_LUMBER:
      return LumberProfileCatalog[headerComponent.lumberProfileType];
    case FramingComponentType.COMPOSITE_LUMBER:
    case FramingComponentType.CUSTOM:
      return lumberProfileDescriptorNew(
        LumberProfileType.CUSTOM,
        headerComponent.name,
        headerComponent.depth,
        headerComponent.width,
        headerComponent.name,
      );
    case FramingComponentType.TRUSS:
      throw new Error(`Truss framing components are not supported as wall headers (component=${headerComponent.id})`);
    case FramingComponentType.I_JOIST:
      throw new Error(`I-joist framing components are not supported as wall headers (component=${headerComponent.id})`);
  }
}

export function getHeaderLumberProfileFromHeaderFramingSettings(
  gapLength: number,
  headerFramingSettings: HeaderFramingSettings,
  headerAssemblies: Table<HeaderAssemblyEntity>,
  framingComponents: Table<FramingComponentEntity>,
): {
  headerProfile: LumberProfileDescriptor;
  headerAssemblyName: string | undefined;
  headerAssemblyId: HeaderAssemblyEntity["id"] | undefined;
  jackStudsFromHeader: number;
  headerCount: number;
} {
  for (const rule of headerFramingSettings.rules) {
    if (gapLength <= rule.maxSpan) {
      const headerAssembly = headerAssemblies[rule.headerAssemblyId];
      const headerComponent =
        headerAssembly !== undefined ? framingComponents[headerAssembly.headerComponentId] : undefined;
      if (headerAssembly === undefined || headerComponent === undefined) {
        console.warn(`Skipping invalid header framing rule reference: ${rule.headerAssemblyId}`);
        continue;
      }
      const headerProfile = getLumberProfileDescriptorFromHeaderComponent(headerComponent);
      return {
        headerProfile,
        headerAssemblyName: headerAssembly.name,
        headerAssemblyId: headerAssembly.id,
        jackStudsFromHeader: rule.jackStudCount,
        headerCount: headerAssembly.headerCount,
      };
    }
  }

  return {
    headerProfile: getHeaderLumberProfile(gapLength),
    headerAssemblyName: undefined,
    headerAssemblyId: undefined,
    jackStudsFromHeader: 1,
    headerCount: 1,
  };
}

/**
 * The following is implementing a rule of thumb to compute the header profiles size:
 * 4' - 2x4
 * 6' - 2x6
 * 8' - 2x8
 * 10' - 2x10
 * 12' - 2x12
 */
export function getHeaderLumberProfile(gapLength: number): LumberProfileDescriptor {
  if (gapLength <= f2i(4)) {
    return LumberProfileCatalog.SPF_2X4;
  } else if (gapLength <= f2i(6)) {
    return LumberProfileCatalog.SPF_2X6;
  } else if (gapLength <= f2i(8)) {
    return LumberProfileCatalog.SPF_2X8;
  } else if (gapLength <= f2i(10)) {
    return LumberProfileCatalog.SPF_2X10;
  } else {
    return LumberProfileCatalog.SPF_2X12;
  }
}

export function isWallFramingAStud(framing: Framing): framing is Stud {
  return (
    framing.type === WallFramingType.STANDARD_STUD ||
    framing.type === WallFramingType.KING_STUD ||
    framing.type === WallFramingType.JACK_STUD ||
    framing.type === WallFramingType.BOTTOM_CRIPPLE_STUD ||
    framing.type === WallFramingType.TOP_CRIPPLE_STUD
  );
}

export function isWallFramingAHeader(framing: Framing): framing is Header {
  return framing.type === WallFramingType.HEADER;
}

export function isWallFramingAPlate(framing: Framing): framing is Plate {
  return (
    framing.type === WallFramingType.BOTTOM_PLATE ||
    framing.type === WallFramingType.TOP_PLATE ||
    framing.type === WallFramingType.BOTTOM_SILL_PLATE ||
    framing.type === WallFramingType.TOP_SILL_PLATE
  );
}

export function isWallFramingABlocking(framing: Framing): framing is Blocking {
  return framing.type === WallFramingType.BLOCKING;
}

export function findBestSingleLumberLength(
  lengthCatalog: LumberLengthDescriptor[],
  length: number,
): LumberLengthDescriptor {
  for (let i = 0, l = lengthCatalog.length; i < l; i++) {
    const standardLength = lengthCatalog[i];
    if (length <= standardLength.length) {
      return standardLength;
    }
  }

  // if not found, return the longest
  // TODO: does this fallback length work for all cases
  // headers and joists probably need to be a different fallback
  return StudLumberLengths[StudLumberLengths.length - 1];
}

export function findStudLumberLength(_studType: string, length: number): LumberLengthDescriptor {
  return findBestSingleLumberLength(StudLumberLengths, length);
}

export function findPlateLumberLength(_plateType: string, _length: number): LumberLengthDescriptor {
  // TODO
  return PlateLumberLengths[0];
}

export function findHeaderLumberLength(_headerType: string, length: number): LumberLengthDescriptor {
  return findBestSingleLumberLength(HeaderLumberLengths, length);
}

export function updateLumberCounters(
  lumberCounters: StandardLumberCounterSet,
  lumberPieces: LumberPiecesByProfile,
  forceSingleLumberPiece: boolean,
  getBestLumberLength: (pieceType: string, length: number) => LumberLengthDescriptor,
) {
  const { profileDescriptor, railsByType } = lumberPieces;
  for (const type in railsByType) {
    const pieces = railsByType[type];

    // sort in descending order (from bigger to smaller)
    pieces.sort((a, b) => {
      return b.length - a.length;
    });

    for (let i = 0, l = pieces.length; i < l; i++) {
      getLumberPiece(
        lumberCounters,
        profileDescriptor,
        type, //not used at all
        forceSingleLumberPiece,
        pieces[i].length,
        getBestLumberLength,
      );
    }
  }
}

export function updateLumberCountersByProfileDescriptor(
  lumberCounters: StandardLumberCounterSet,
  profileDescriptor: LumberProfileDescriptor,
  rails: LumberRail[],
  type: string, //not used at all
  forceSingleLumberPiece: boolean,
  getBestLumberLength: (pieceType: string, length: number) => LumberLengthDescriptor,
) {
  const pieces = rails;
  // sort in descending order (from bigger to smaller)
  pieces.sort((a, b) => {
    return b.length - a.length;
  });

  for (let i = 0, l = pieces.length; i < l; i++) {
    getLumberPiece(
      lumberCounters,
      profileDescriptor,
      type,
      forceSingleLumberPiece,
      pieces[i].length,
      getBestLumberLength,
    );
  }
}

type LumberRailAdderFn = (rails: LumberRail[]) => boolean;

export function addWallFramingMember(
  framing: Framing[],
  type: WallFramingType,
  profileDescriptor: LumberProfileDescriptor,
  adder: LumberRailAdderFn,
  framingMeta?: WallFramingMeta,
): void {
  const rails: LumberRail[] = [];
  const added = adder(rails);
  if (!added) {
    return;
  }
  const stud = {
    type,
    profileDescriptor,
    rails,
    meta: {
      ...framingMeta,
    },
  };
  framing.push(stud as Framing);
}

export interface GroupedWallFraming {
  readonly lumberRails: LumberRail[];
  readonly profileDescriptor: LumberProfileDescriptor;
}

export function groupFramingByProfileDescriptor(framings: Framing[]): GroupedWallFraming[] {
  const grouped: GroupedWallFraming[] = [];
  for (const framing of framings) {
    const profileDescriptor = framing.profileDescriptor;
    const lumberRails = framing.rails;
    const existingGroup = grouped.find((group) => isSameProfileDescriptor(group.profileDescriptor, profileDescriptor));
    if (existingGroup) {
      existingGroup.lumberRails.push(...lumberRails);
    } else {
      grouped.push({ profileDescriptor, lumberRails });
    }
  }
  return grouped;
}
